import { useEffect, useState } from 'react';
import { MeProvider, useMe } from './lib/me';
import { RouterProvider, matchPath, navigate, useLocation } from './lib/router';
import { api } from './lib/api';
import { Shell } from './components/Shell';
import { Button, Loading, ToastProvider } from './components/ui';
import { LoginPage } from './pages/Login';
import { HomePage } from './pages/Home';
import { SectionPage } from './pages/Section';
import { ToolHost } from './pages/ToolHost';
import { ProfilePage } from './pages/Profile';
import { AppearancePage } from './pages/Appearance';
import { NotFound } from './pages/NotFound';
import { AdminLayout, ADMIN_PAGES } from './admin/AdminLayout';
import { AdminOverview } from './admin/Overview';
import { UserDetailPage, UsersPage } from './admin/Users';
import { RolesPage } from './admin/Roles';
import { ClearancePage } from './admin/Clearance';
import { DiscordPage } from './admin/Discord';
import { BackupsPage } from './admin/Backups';
import { DataSourcesPage } from './admin/DataSources';
import { LogsPage } from './admin/Logs';
import { RevisionsPage } from './admin/Revisions';
import { SettingsPage } from './admin/Settings';

function Inactive({ status, inGuild, refresh }: { status: string; inGuild: boolean; refresh: () => Promise<void> }) {
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const left = status !== 'suspended' && !inGuild;
  const recheck = async () => {
    setChecking(true);
    try {
      const r = await api.post<{ status: string }>('/api/auth/recheck');
      if (r.status === 'active') { await refresh(); return; }
      setNote('Still not a member of the Discord server. If you just rejoined, give it a moment and try again.');
    } catch { setNote('Could not check right now. Try again shortly.'); }
    setChecking(false);
  };
  return (
    <div className="login">
      <div className="login-card">
        <img src="/logo.png" alt="" width={96} height={96} className="login-logo" />
        <h1>{status === 'suspended' ? 'Access suspended' : left ? 'Account locked' : 'Account inactive'}</h1>
        <p className="muted">{status === 'suspended'
          ? 'An admin has suspended your dashboard access. Reach out to Ravenbound leadership on Discord.'
          : left
            ? 'You are no longer a member of the Ravenbound Discord server, so your dashboard account is locked. Your account and data are kept: you’ll get access back as soon as you rejoin the server and your membership syncs.'
            : 'Your account is inactive. Contact an admin.'}</p>
        {note && <p className="small warn-text">{note}</p>}
        <div className="row-actions" style={{ justifyContent: 'center' }}>
          {left && <Button variant="primary" icon="refresh" busy={checking} onClick={recheck}>I’ve rejoined, check again</Button>}
          <Button onClick={async () => { await api.post('/api/auth/logout'); location.href = '/login'; }}>Sign out</Button>
        </div>
      </div>
    </div>
  );
}

function AdminRoutes({ path }: { path: string }) {
  const { me, can } = useMe();
  if (!me?.access.adminPanel) return <NotFound />;
  const user = matchPath('/admin/users/:id', path);
  const page = path.split('/')[2] ?? '';
  const def = ADMIN_PAGES.find((p) => p.id === page);
  if (!def || (def.perm && !can(def.perm))) return <AdminLayout><NotFound /></AdminLayout>;
  const body = user ? <UserDetailPage id={user.id} />
    : page === '' ? <AdminOverview />
    : page === 'users' ? <UsersPage />
    : page === 'roles' ? <RolesPage />
    : page === 'clearance' ? <ClearancePage />
    : page === 'discord' ? <DiscordPage />
    : page === 'datasources' ? <DataSourcesPage />
    : page === 'backups' ? <BackupsPage />
    : page === 'logs' ? <LogsPage />
    : page === 'revisions' ? <RevisionsPage />
    : page === 'settings' ? <SettingsPage /> : <NotFound />;
  return <AdminLayout>{body}</AdminLayout>;
}

function Routes() {
  const { me, loading, error, refresh } = useMe();
  const { path } = useLocation();

  useEffect(() => {
    if (!loading && !me && path !== '/login') navigate('/login', true);
    if (!loading && me && path === '/login') navigate('/', true);
  }, [loading, me, path]);

  if (loading) return <div className="splash"><img src="/logo.png" alt="" width={96} height={96} /><Loading label="Connecting" /></div>;
  if (!me) {
    if (error && error.status !== 401) {
      return <div className="splash"><p>Could not reach the dashboard: {error.message}</p><Button onClick={refresh}>Retry</Button></div>;
    }
    return <LoginPage onSignedIn={async () => { await refresh(); navigate('/', true); }} />;
  }
  if (me.user.status !== 'active') return <Inactive status={me.user.status} inGuild={!!me.user.inGuild} refresh={refresh} />;

  let page;
  let m: Record<string, string> | null;
  if (path === '/' || path === '/login') page = <HomePage />;
  else if ((m = matchPath('/s/:id', path))) page = <SectionPage id={m.id} />;
  else if ((m = matchPath('/t/:toolId/*', path))) page = <ToolHost toolId={m.toolId} subPath={m['*']} />;
  else if (path === '/profile') page = <ProfilePage />;
  else if (path === '/appearance') page = <AppearancePage />;
  else if (path === '/admin' || path.startsWith('/admin/')) page = <AdminRoutes path={path} />;
  else page = <NotFound />;
  return <Shell>{page}</Shell>;
}

export function App() {
  return (
    <RouterProvider>
      <ToastProvider>
        <MeProvider>
          <Routes />
        </MeProvider>
      </ToastProvider>
    </RouterProvider>
  );
}
